import React, { createContext, useContext, useEffect, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';

/**
 * CurrentEventContext
 * -------------------
 * Holds the *active* event for the logged-in user plus the list of all
 * events they are registered for (via event_orders).
 * Defaults to the most recent event the user registered for.
 *
 * Usage:
 *   const { currentEvent, setCurrentEvent, events } = useCurrentEvent();
 */
const CurrentEventContext = createContext({
  currentEvent: null,
  events: [],
  setCurrentEvent: () => {},
});

export const useCurrentEvent = () => useContext(CurrentEventContext);

export const CurrentEventProvider = ({ children }) => {
  const [events, setEvents] = useState([]);      // all events for this user
  const [currentEvent, _setCurrentEvent] = useState(null);
  // const navigate = useNavigate();

  // Query Supabase for the user->event list once on mount
  useEffect(() => {
    const fetchEvents = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) return;

      // Get event orders with created_at timestamp to find most recent
      const { data: orders, error: ordersError } = await supabase
        .from('event_orders')
        .select('event_id, created_at')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (ordersError) {
        console.error('Failed to load user events', ordersError);
        return;
      }

      const eventIds = (orders || []).map((row) => row.event_id);
      if (eventIds.length === 0) return;

      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select('*')
        .in('id', eventIds);

      if (eventsError) {
        console.error('Failed to fetch events', eventsError);
      } else {
        setEvents(eventsData || []);

        // Set current event to the most recent one by default
        if (eventsData?.length > 0 && !currentEvent) {
          // Find the event corresponding to the most recent order
          const mostRecentEventId = orders[0].event_id;
          const mostRecentEvent = eventsData.find(e => e.id === mostRecentEventId);
          
          if (mostRecentEvent) {
            _setCurrentEvent(mostRecentEvent);
            localStorage.setItem('pm_current_event', mostRecentEvent.id);
          }
        }
      }
    };
    fetchEvents();
  }, []);

  const setCurrentEvent = (evt) => {
    _setCurrentEvent(evt);
    // Persist selection for reloads
    if (evt?.id) localStorage.setItem('pm_current_event', evt.id);
  };

  // On mount: load persisted event if available
  useEffect(() => {
    const saved = localStorage.getItem('pm_current_event');
    if (saved && events.length > 0) {
      const match = events.find((e) => e.id === saved);
      if (match) _setCurrentEvent(match);
    }
  }, [events]);

  // Previously this context auto-navigated to the active event. That logic has
  // been removed so consumers can decide how/when to change routes.

  return (
    <CurrentEventContext.Provider value={{ currentEvent, setCurrentEvent, events }}>
      {children}
    </CurrentEventContext.Provider>
  );
}; 